import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiClient, ApiError } from '../../api/client';
import type {
  ClientOptions,
  ClipboardRender,
  Field,
  FormSchema,
  FormValues,
  PreviewResponse,
  Recipient,
  SignedInUser,
} from '../../api/types';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { CopyPanel } from './CopyPanel';
import { FormSection } from './FormSection';
import { IssueList } from './IssueList';
import { PreviewPane } from './PreviewPane';
import { SendPanel } from './SendPanel';

const PREVIEW_DEBOUNCE_MS = 450;

interface ComposerProps {
  api: ApiClient;

  /** Null when there is no mailbox to create a draft in — see LocalApp.tsx. */
  draftUnavailableReason: string | null;

  onConsentRequired: (scopes: string[]) => void;
}

export function Composer({ api, draftUnavailableReason, onConsentRequired }: ComposerProps) {
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [values, setValues] = useState<FormValues>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  // Keys the user has typed into. A pre-filled control follows its source until it appears here,
  // and then never again — see the `prefillFrom` note in api/types.ts.
  const editedByHand = useRef<Set<string>>(new Set());

  // Until the API answers, assume the stricter answer to both: offering an action the server would
  // refuse is worse than briefly withholding one it would allow, and a section that appears and then
  // vanishes is worse than one that arrives a moment late.
  const [clientOptions, setClientOptions] = useState<ClientOptions>({
    showCopyToClipboard: false,
    requireCompleteFormToCopy: true,
  });

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Fetched ahead of the click rather than on it. Copying works by selecting the rendered email and
  // letting the browser serialise it, and that only runs while the click's gesture is still in effect
  // — so there is no room to await a render in the handler. Costs one extra render per settle, which
  // on a form somebody fills in a few times a week is a fair price for a button that always works.
  const [clipboard, setClipboard] = useState<ClipboardRender | null>(null);

  // Nothing is reported while the form is being filled in. Every required field is empty to begin
  // with and half a postcode is not yet a wrong postcode, so validating as someone types means
  // telling them off for not having finished — the form flashes red on the way to being correct.
  // Errors appear when they ask for something to be done with the email, and then stay live so the
  // list shrinks as they fix it.
  const [validationRevealed, setValidationRevealed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const templates = await api.listTemplates(controller.signal);

        if (templates.length === 0) {
          setLoadError('No templates are available. Ask an administrator to check the API health endpoint.');
          return;
        }

        const loaded = await api.getFormSchema(templates[0].id, controller.signal);
        setSchema(loaded);

        // Allowed to fail like the profile below: losing this leaves the stricter default in place,
        // which is a worse form to fill in but never a wrong one.
        try {
          setClientOptions(await api.getClientOptions(controller.signal));
        } catch {
          // Keep the default.
        }

        const initial: FormValues = {};
        for (const field of loaded.fields) {
          initial[field.key] = field.default ?? '';
        }

        // Allowed to fail: a missing phone number in the directory should not block the form.
        let profile: SignedInUser | null = null;
        try {
          profile = await api.getSignedInUser(controller.signal);
        } catch (error) {
          if (error instanceof ApiError && error.consentRequired) {
            onConsentRequired(error.consentScopes);
          }
        }

        if (profile) {
          applyPrefill(initial, loaded, profile);
        }

        setValues(initial);
      } catch (error) {
        if (controller.signal.aborted) return;
        setLoadError(error instanceof Error ? error.message : 'Could not load the template.');
      }
    })();

    return () => controller.abort();
  }, [api, onConsentRequired]);

  const debouncedValues = useDebouncedValue(values, PREVIEW_DEBOUNCE_MS);

  // Re-render the preview whenever typing settles. The effect's cleanup aborts the in-flight
  // request, so a slow response for older values cannot overwrite a newer render.
  useEffect(() => {
    if (!schema || Object.keys(debouncedValues).length === 0) return;

    const controller = new AbortController();
    setPreviewLoading(true);

    api
      .preview(schema.id, debouncedValues, controller.signal)
      .then((result) => {
        setPreview(result);
        setPreviewError(null);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        if (error instanceof ApiError && error.consentRequired) {
          onConsentRequired(error.consentScopes);
          return;
        }

        setPreviewError(error instanceof Error ? error.message : 'Could not render the preview.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewLoading(false);
      });

    return () => controller.abort();
  }, [api, schema, debouncedValues, onConsentRequired]);

  // Answered from the form rather than the preview, so the copy button unlocks on the keystroke that
  // completes the form instead of a debounce-and-a-round-trip later. The API checks it again anyway.
  const missingRequired = useMemo(
    () => (schema === null ? [] : requiredFieldsStillEmpty(schema, values)),
    [schema, values],
  );

  // Ghost text for a control whose value was meant to arrive with a choice and did not — a clinic
  // the directory holds no address for. Recomputed as the form changes rather than stored, because
  // it is a statement about what is selected right now: pick a clinic that does have an address and
  // the control fills in, and the text has nothing left to say.
  const unfilledPlaceholders = useMemo(
    () => (schema === null ? {} : describeUnfilled(schema, values)),
    [schema, values],
  );

  const copyBlockedReason = clientOptions.requireCompleteFormToCopy
    ? describeMissing(missingRequired)
    : null;

  // Keep the copy ready. Same debounce as the preview. Skipped when the section is hidden, so a
  // feature nobody can see costs no renders, and skipped while the copy is blocked, because the
  // endpoint refuses an incomplete email — that would be a stream of 400s nobody is waiting for.
  useEffect(() => {
    if (
      !schema ||
      !clientOptions.showCopyToClipboard ||
      copyBlockedReason !== null ||
      Object.keys(debouncedValues).length === 0
    ) {
      setClipboard(null);
      return;
    }

    const controller = new AbortController();

    api
      .renderForClipboard(schema.id, debouncedValues, controller.signal)
      .then(setClipboard)
      // A failure here is not worth reporting: the button falls back to fetching on click and will
      // surface whatever went wrong then, next to the action the user actually took.
      .catch(() => {
        if (!controller.signal.aborted) setClipboard(null);
      });

    return () => controller.abort();
  }, [api, schema, debouncedValues, copyBlockedReason, clientOptions.showCopyToClipboard]);

  const handleChange = useCallback(
    (key: string, value: string) => {
      // Editing a control is what stops it following its source, so the mark goes on before the
      // value does. A ref rather than state: nothing renders differently because of it, and reading
      // it inside the updater below needs the current set, not the one from this render.
      editedByHand.current.add(key);

      setValues((current) => applyDependents({ ...current, [key]: value }, schema, key, editedByHand.current));
    },
    [schema],
  );

  const revealValidation = useCallback(() => setValidationRevealed(true), []);

  const createDraft = useCallback(
    (request: { to: Recipient[]; acknowledgeWarnings: boolean }) => {
      if (!schema) throw new Error('The template is still loading.');
      return api.createDraft(schema.id, { ...request, values });
    },
    [api, schema, values],
  );

  const renderForClipboard = useCallback(() => {
    if (!schema) throw new Error('The template is still loading.');
    return api.renderForClipboard(schema.id, values);
  }, [api, schema, values]);

  const visibleIssues = useMemo(
    () => (validationRevealed ? (preview?.issues ?? []) : []),
    [preview, validationRevealed],
  );

  const fieldErrors = useMemo(() => {
    const map: Record<string, string[]> = {};

    for (const issue of visibleIssues) {
      if (issue.severity !== 'error' || !issue.fieldKey) continue;
      (map[issue.fieldKey] ??= []).push(issue.message);
    }

    return map;
  }, [visibleIssues]);

  // Based on every error, not just the visible ones — the draft is not ready either way.
  const hasErrors = (preview?.issues ?? []).some((i) => i.severity === 'error');

  if (loadError) {
    return (
      <div className="page-message page-message--error">
        <h2>Could not load the template</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  if (!schema) {
    return <div className="page-message">Loading the template…</div>;
  }

  const sections = schema.groups
    .filter((g) => g.parent === null)
    .sort((a, b) => a.order - b.order)
    .map((section) => ({
      section,
      subsections: schema.groups
        .filter((g) => g.parent === section.id)
        .sort((a, b) => a.order - b.order)
        .map((subsection) => ({
          subsection,
          fields: schema.fields
            .filter((f) => f.group === subsection.id)
            .sort((a, b) => a.order - b.order),
        }))
        .filter((s) => s.fields.length > 0),
    }))
    .filter((s) => s.subsections.length > 0);

  return (
    <div className="workspace">
      <form className="form" onSubmit={(e) => e.preventDefault()} noValidate>
        <IssueList issues={visibleIssues} />

        {sections.map(({ section, subsections }) => (
          <div className="group" key={section.id}>
            <h2 className="group__legend">{section.label}</h2>

            {subsections.map(({ subsection, fields }) => (
              <FormSection
                key={subsection.id}
                group={subsection}
                fields={fields}
                values={values}
                fieldErrors={fieldErrors}
                unfilledPlaceholders={unfilledPlaceholders}
                onChange={handleChange}
              />
            ))}
          </div>
        ))}

        <SendPanel
          values={values}
          hasErrors={hasErrors}
          draftUnavailableReason={draftUnavailableReason}
          onRevealErrors={revealValidation}
          createDraft={createDraft}
          onConsentRequired={onConsentRequired}
        />

        {clientOptions.showCopyToClipboard && (
          <CopyPanel
            blockedReason={copyBlockedReason}
            html={clipboard?.html ?? null}
            warnings={clipboard?.issues.filter((i) => i.severity === 'warning') ?? []}
            render={renderForClipboard}
            onBlocked={revealValidation}
          />
        )}
      </form>

      <PreviewPane preview={preview} isLoading={previewLoading} error={previewError} />
    </div>
  );
}

/**
 * The required fields with nothing in them yet.
 *
 * A control the manifest greys out does not count: ticking "Closed" for a day disables that day's
 * times, and the server drops their required flag for the same reason. Demanding a value from a box
 * nobody can type into would leave the form permanently one field short of ready.
 */
function requiredFieldsStillEmpty(schema: FormSchema, values: FormValues): Field[] {
  return schema.fields.filter((field) => {
    if (!field.required) return false;
    if (field.disabledWhen !== null && values[field.disabledWhen] === 'true') return false;

    return (values[field.key] ?? '').trim().length === 0;
  });
}

/** Tooltip copy for a button that is not ready yet. Naming the field beats "something is missing". */
function describeMissing(missing: Field[]): string | null {
  if (missing.length === 0) return null;

  return missing.length === 1
    ? `“${missing[0].label}” is still empty. Fill it in to copy the email.`
    : `${missing.length} required fields are still empty. Fill them all in to copy the email.`;
}

/**
 * Adds `hours` to an "HH:mm" value, wrapping at midnight. Returns the input unchanged if it is not a
 * time — an empty control stays empty rather than becoming 01:00.
 */
function shiftClockTime(value: string, hours: number): string {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!match) return value;

  const shifted = (Number(match[1]) + hours + 24) % 24;

  return `${String(shifted).padStart(2, '0')}:${match[2]}`;
}

/**
 * The value a pre-filled field should hold right now: from the first of its sources that is on the
 * page and non-empty, or empty if none is.
 *
 * "On the page" is the same test the form uses to decide whether to draw a control — a source gated
 * behind a checkbox counts only while that box is ticked. This is what lets `["hoursMonEnd2",
 * "hoursMonEnd"]` mean "the end of the second shift, or of the only one": clearing the split box
 * drops the second shift out of consideration even if a stale time is still sitting in it.
 */
function resolvePrefill(field: Field, values: FormValues, fieldsByKey: ReadonlyMap<string, Field>): string {
  for (const sourceKey of field.prefillFrom) {
    const source = fieldsByKey.get(sourceKey);
    const onPage = !source?.shownWhen || values[source.shownWhen] === 'true';
    const value = values[sourceKey] ?? '';

    if (onPage && value) return value;
  }

  return '';
}

/**
 * Pushes a changed value into every field that depends on it.
 *
 * Three kinds of dependency. Two of them copy a value that is already on the form and stop at the
 * first hand edit; the third comes attached to the choice itself and does not.
 *
 * `prefillFrom` copies another control — a training day spent in the clinic runs to the clinic's own
 * hours. `shiftsWith` re-reads a field's default in the zone the sender picked — a national training
 * session happens at one moment, which is a different wall-clock time in each region. A clinic's
 * opening hours are local by definition and are never shifted; the manifest refuses to declare both
 * on one field.
 *
 * Both stop following the moment someone types over the control, permanently: at that point they know
 * something the source does not. An option's `fills` is the opposite and deliberately so — picking a
 * different clinic makes the address on screen wrong no matter who typed it, and a corrected address
 * left standing under another clinic's name is the one outcome worth ruling out.
 *
 * A pre-filled field re-resolves when any of its sources changes, and also when the checkbox that
 * gates one of them does — ticking "split shift" is what makes the second shift a candidate at all.
 *
 * Shifting works from the default rather than the value on screen, so changing zone twice does not
 * apply the offset twice. The manifest forbids chains, so one pass is enough. Returns the same object
 * when nothing moved, so a keystroke in a field nobody depends on costs no extra render work.
 */
function applyDependents(
  values: FormValues,
  schema: FormSchema | null,
  changedKey: string,
  editedByHand: ReadonlySet<string>,
): FormValues {
  if (!schema) return values;

  const fieldsByKey = new Map(schema.fields.map((f) => [f.key, f]));
  const incoming = values[changedKey] ?? '';
  const changed = fieldsByKey.get(changedKey);
  const chosen = changed?.options.find((o) => o.value === incoming);
  const offsetHours = chosen?.offsetHours ?? 0;

  const gatesASourceOf = (field: Field) =>
    field.prefillFrom.some((key) => fieldsByKey.get(key)?.shownWhen === changedKey);

  let next = values;

  for (const field of schema.fields) {
    if (editedByHand.has(field.key)) continue;

    let updated: string | null = null;

    if (field.prefillFrom.includes(changedKey) || gatesASourceOf(field)) {
      updated = resolvePrefill(field, next, fieldsByKey);
    } else if (field.shiftsWith === changedKey && field.default) {
      updated = shiftClockTime(field.default, offsetHours);
    }

    if (updated === null || next[field.key] === updated) continue;

    next = { ...next, [field.key]: updated };
  }

  // Applied last, so what the chosen option carries wins over a prefill of the same field. Nothing on
  // screen is cleared when the control goes back to "Choose…": there is no option to read a value
  // from, and blanking a required field somebody is midway through answering would be its own kind of
  // wrong. The manifest refuses to let one field be both filled and pre-filled, so this only ever
  // overwrites something the sender typed themselves.
  for (const [key, filled] of Object.entries(chosen?.fills ?? {})) {
    if (next[key] === filled) continue;

    next = { ...next, [key]: filled };
  }

  return next;
}

/**
 * Ghost text for every control an option was supposed to fill and left empty, by field key.
 *
 * A directory is allowed to be incomplete — eleven of the clinics open today have no street address
 * recorded — and a control that simply stays blank reads as the form having lost the value rather
 * than the directory never having had it. Naming the clinic is the point: "no address on record"
 * alone leaves the sender wondering which of the two things they picked is at fault.
 *
 * Only for a choice that has actually been made. Before that the control is empty because nobody has
 * answered yet, which its own placeholder already covers.
 */
function describeUnfilled(schema: FormSchema, values: FormValues): Record<string, string> {
  const described: Record<string, string> = {};

  for (const source of schema.fields) {
    const chosen = source.options.find((o) => o.value === (values[source.key] ?? ''));
    if (!chosen) continue;

    for (const [key, filled] of Object.entries(chosen.fills)) {
      if (filled.length > 0) continue;

      const target = schema.fields.find((f) => f.key === key);
      if (!target?.unfilledPlaceholder) continue;

      described[key] = target.unfilledPlaceholder.replace('{option}', chosen.label);
    }
  }

  return described;
}

/**
 * Copies directory-profile values into the fields the manifest nominates. Never overwrites a value
 * that came from a field default — the template author's choice wins over the directory.
 */
function applyPrefill(values: FormValues, schema: FormSchema, profile: SignedInUser): void {
  const fromProfile: Record<string, string | null> = {
    displayName: profile.displayName,
    email: profile.email,
    phone: profile.phone,
    jobTitle: profile.jobTitle,
  };

  for (const [source, fieldKey] of Object.entries(schema.prefillFromSignedInUser)) {
    const value = fromProfile[source];
    if (value && !values[fieldKey]) {
      values[fieldKey] = value;
    }
  }
}
